import { getDiscountName } from "@repo/shopify/utils/order";
import {
  checkProductLimitReached,
  checkProductLimitReachedCaa,
  checkVariantLimitReached,
  checkVariantLimitReachedCaa,
} from "@repo/shopify/utils/product";
import type { CartLine, ComponentizableCartLine } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useMemo } from "react";

import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Price from "@atoms/Price";

import Stepper from "@molecules/Stepper";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import type useCartQuery from "@hooks/useCartQuery";
import useLanguage from "@hooks/useLanguage";

import { useCartStore } from "@store/cart";
import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

interface IProps {
  node: CartLine | ComponentizableCartLine;
  updateCartLine: ReturnType<typeof useCartQuery>["updateCartLine"];
  removeCartLine: ReturnType<typeof useCartQuery>["removeCartLine"];
  updateCartLineLoading: boolean;
  removeCartLineLoading: boolean;
}

export default function CartLineItem({
  node,
  updateCartLine,
  removeCartLine,
  updateCartLineLoading,
  removeCartLineLoading,
}: IProps) {
  const { cart } = useCartStore();
  const { customer, caaCustomer } = useCustomerStore();
  const { openToast } = useLayoutStore();
  const { lang } = useLanguage();

  const color = useMemo(
    () => node.merchandise.selectedOptions.find((option) => option.name === "Color")?.value || "",
    [node.merchandise],
  );

  const size = useMemo(
    () => node.merchandise.selectedOptions.find((option) => option.name === "Size")?.value || "",
    [node.merchandise],
  );

  const handleRemove = async () => {
    if (updateCartLineLoading || removeCartLineLoading) return;
    await removeCartLine({ variables: { cartId: cart.id, lineIds: [node.id] } });
  };

  const handleChangeQuantity = (targetQuantity: number) => {
    if (updateCartLineLoading || removeCartLineLoading) return;

    const isProductLimitReached =
      lang === "jp"
        ? checkProductLimitReachedCaa({ cart, customer: caaCustomer, product: node.merchandise.product })
        : checkProductLimitReached({ cart, customer, product: node.merchandise.product });

    const isVariantLimitReached =
      lang === "jp"
        ? checkVariantLimitReachedCaa({
            cart,
            customer: caaCustomer,
            product: node.merchandise.product,
            selectedOption: node.merchandise.id,
          })
        : checkVariantLimitReached({
            cart,
            customer,
            product: node.merchandise.product,
            selectedOption: node.merchandise.id,
          });

    if (targetQuantity > (node.merchandise?.quantityAvailable || 99)) {
      openToast(NOTIFICATIONS.ERROR_EXCEED_QTY);
      return;
    }

    if (node.quantity <= targetQuantity && (isProductLimitReached || isVariantLimitReached)) {
      openToast(NOTIFICATIONS.ERROR_EXCEED_QTY);
      return;
    }

    void updateCartLine({ variables: { cartId: cart.id, lines: [{ id: node.id, quantity: targetQuantity }] } });
  };

  const isGift = node.merchandise.product.tags.includes("Gift");

  return (
    <div className="pc:gap-5 flex gap-2.5">
      <Link className="w-20 min-w-20" href={`${URLS.PRODUCTS}/${node.merchandise.product.handle}`}>
        <Image
          height={node.merchandise.image?.height || 1707}
          src={node.merchandise.image?.url || ""}
          width={node.merchandise.image?.width || 1280}
        />
      </Link>
      <div className="flex w-full gap-2.5">
        <div className="flex w-full flex-col justify-between gap-2.5">
          <span>{`${node.merchandise.product.title} ${color} / ${size}`}</span>
          {!isGift && (
            <Stepper
              handleChange={handleChangeQuantity}
              handleRemove={handleRemove}
              isGift={node.merchandise.product.tags.includes("Gift")}
              quantity={node.quantity}
              quantityAvailable={node.merchandise?.quantityAvailable || 99}
            />
          )}
        </div>

        <div className="flex w-fit flex-col items-end">
          <Price
            compareAtPrice={Number(node.cost.compareAtAmountPerQuantity?.amount || "0") * node.quantity}
            direction="vertical"
            price={Number(node.cost.totalAmount.amount || "0")}
            variant="secondary"
          />

          {node.discountAllocations.map((discount) => (
            <span className="text-xs font-normal whitespace-nowrap lowercase" key={getDiscountName(discount)}>
              {`(${getDiscountName(discount).toLowerCase()})`}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
